'use client';

import parse from 'autosuggest-highlight/parse';
import { useRef, useState, useEffect } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { Controller, useFormContext } from 'react-hook-form';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { debounce } from '@mui/material/utils';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import { CONFIG } from 'src/global-config';
import { useTranslate } from 'src/locales';

// ----------------------------------------------------------------------

export function RHFAutocompleteAddress({
  name,
  label,
  onChange,
  helperText,
  hiddenLabel,
  placeholder,
  ...other
}) {
  const { t } = useTranslate();

  const [options, setOptions] = useState([]);

  const autocompleteSuggestion = useRef(null);

  const { control, setValue } = useFormContext();

  const search = debounce(async (input) => {
    const response = input
      ? await autocompleteSuggestion.current?.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
        })
      : null;
    setOptions(response?.suggestions ?? []);
  }, 500);

  const isOptionEqualToValue = (option, value) => value === option?.placePrediction?.text?.text;
  const getOptionLabel = (option) =>
    typeof option === 'string' ? option : option?.placePrediction?.text?.text;

  const setValues = (addressData) => {
    setValue('city', addressData.city, { shouldValidate: true });
    setValue('state', addressData.state, { shouldValidate: true });
    setValue('country', addressData.country, { shouldValidate: true });
    setValue('geocoder', { lat: 0, lng: 0 }, { shouldValidate: true });
    setValue('zipCode', addressData.zipCode, { shouldValidate: true });
  };

  const handleOnChange = async (newValue) => {
    const fullAddress = newValue?.placePrediction?.text?.text;
    const addressData = { country: '', state: '', city: '', zipCode: '' };

    setValue(name, fullAddress, { shouldValidate: true });

    if (!newValue?.placePrediction.placeId) {
      setValues(addressData);
      onChange?.(null);
      return;
    }

    const { place } = await newValue.placePrediction
      .toPlace()
      .fetchFields({ fields: ['addressComponents'] });

    place.addressComponents.forEach((components) => {
      if (components.types.includes('country')) {
        addressData.country = components.longText ?? '';
      } else if (components.types.includes('administrative_area_level_1')) {
        addressData.state = components.longText ?? '';
      } else if (components.types.includes('locality')) {
        addressData.city = components.longText ?? '';
      } else if (components.types.includes('postal_code')) {
        addressData.zipCode = components.longText ?? '';
      }
    });

    setValues(addressData);
    onChange?.(addressData);
  };

  const renderOption = (props, option) => {
    const matches = option?.placePrediction?.mainText?.matches ?? [];

    const parts = parse(
      option?.placePrediction?.mainText?.text ?? '',
      matches.map((match) => [match.startOffset, match.endOffset])
    );

    return (
      <li {...props} key={option?.placePrediction?.placeId}>
        <Grid container sx={{ alignItems: 'center' }}>
          <Grid sx={{ display: 'flex', width: 44 }}>
            <LocationOnIcon sx={{ color: 'text.secondary' }} />
          </Grid>
          <Grid sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
            {parts.map((part, index) => (
              <Box
                key={index}
                component="span"
                sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
              >
                {part.text}
              </Box>
            ))}
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {option?.placePrediction?.secondaryText?.text}
            </Typography>
          </Grid>
        </Grid>
      </li>
    );
  };

  const renderInput = (params, error) => (
    <TextField
      fullWidth
      {...params}
      label={label}
      error={!!error}
      hiddenLabel={hiddenLabel}
      placeholder={placeholder}
      helperText={error?.message ?? helperText}
      slotProps={{
        input: {
          ...params.InputProps,
          autoComplete: 'new-password',
        },
      }}
    />
  );

  const initPlaces = async () => {
    if (window.google.maps && !autocompleteSuggestion.current) {
      autocompleteSuggestion.current =
        window.google.maps.places ?? (await window.google.maps.importLibrary('places'));
      search(control._formValues.fullAddress);
    }
  };

  useEffect(() => {
    initPlaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [control._formValues.fullAddress]);

  return (
    <APIProvider apiKey={CONFIG.gmapsApiKey}>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Autocomplete
            {...field}
            options={options}
            renderOption={renderOption}
            getOptionLabel={getOptionLabel}
            noOptionsText={t('No locations')}
            id={`google-autocomplete-${name}`}
            isOptionEqualToValue={isOptionEqualToValue}
            renderInput={(params) => renderInput(params, error)}
            onChange={(event, newValue) => handleOnChange(newValue)}
            onInputChange={(event, newInputValue) => search(newInputValue)}
            {...other}
          />
        )}
      />
    </APIProvider>
  );
}
